import { ErrorResponseV2Error } from '@flipster/utils/axios';
import { OrderMarginType, V2OrderSideType } from '@flipster/utils/types/base';
import { TimestampString, UUID } from '@flipster/utils/typescript';

import {
  ConditionalOrderTypeEnum,
  OneWayOrderCreationRequestBase,
  OpenedPosition,
  SpotMarketOrderAsset,
  Timestamp,
  TradeRequestBase,
} from '../../types';
import { TriggerPriceTypeEnum } from '../history/types';

type TriggerDirectionType = 'Up' | 'Down';

/**
 * Execution type for TP/SL orders.
 * Defaults to `Market` when omitted (backward-compatible).
 * BBO is expressed as `Limit` + `priceMatchStrategy` (see `orderTp/SlPriceMatchStrategy`),
 * mirroring how regular orders already handle BBO.
 */
export type TpslOrderType = 'Market' | 'Limit';

/**
 * Execution type for Trigger orders. Defaults to `Market` when omitted (preserves
 * existing StopMarket behavior). `Limit` requires either `orderPrice` (fixed limit
 * price) or `priceMatchStrategy` (BBO).
 */
export type TriggerOrderExecutionType = 'Market' | 'Limit';

export const triggerPriceTypeEnum = {
  last: 'LAST',
  mark: 'MARK',
} as const;

/** Retail REST trigger price basis. Omitted → server defaults to LAST. */
export type TriggerPriceType = (typeof triggerPriceTypeEnum)[keyof typeof triggerPriceTypeEnum];

/** True when trigger basis is Mark (REST `MARK` or socket/history `TRIGGER_PRICE_TYPE_MARK`). */
export function isMarkTriggerPriceType(value: string | null | undefined): boolean {
  return value === triggerPriceTypeEnum.mark || value === TriggerPriceTypeEnum.MarkPrice;
}

interface TpSlTriggerPriceTypeFields {
  orderTpTriggerPriceType?: TriggerPriceType;
  orderSlTriggerPriceType?: TriggerPriceType;
}

// timestamp: Request 보내는 시점의 client 시간
// refServerTimestamp: 가장 최근 bbo_price_timestamp
// refClientTimestamp: 가장 최근 bbo_price_timestamp를 받았을 때 client 시간

export interface PostPositionParams extends TradeRequestBase, TpSlTriggerPriceTypeFields {
  side: V2OrderSideType;
  price?: string;
  orderTpTriggerPrice?: string;
  orderSlTriggerPrice?: string;
  // Order TP/SL execution type (retail-api uses PascalCase). Defaults to `Market` when omitted.
  orderTpOrderType?: TpslOrderType;
  // Required when `orderTpOrderType` is `Limit` and BBO is not used. Null otherwise.
  // Mutually exclusive with `orderTpPriceMatchStrategy`.
  orderTpLimitPrice?: string | null;
  // Only valid with `orderTpOrderType === 'Limit'`. Used in place of a fixed
  // `orderTpLimitPrice` to place the triggered Limit at the best bid/offer (BBO).
  orderTpPriceMatchStrategy?: string;
  orderSlOrderType?: TpslOrderType;
  orderSlLimitPrice?: string | null;
  orderSlPriceMatchStrategy?: string;
  leverage?: number;
  amount?: string;
  qty?: string;
  attribution?: string;
  marginType: OrderMarginType;
  orderType?: string;
  priceMatchStrategy?: string; // defines BBO strategy, only pass it If price is null
  maxSlippageTolerance?: string;
  marketOrderLimitPrice?: string;
}

export interface PostPositionResponse {
  position: OpenedPosition;
  order: OrderSuccess;
  timestamp: string;
}

export interface PostAddPositionParams extends Omit<PostPositionParams, 'marginType'> {}

type TriggerPriceOrder = {
  triggerPrice: string;
  triggerDirection: TriggerDirectionType;
  /** Trigger evaluation price basis. Omitted → server defaults to LAST. */
  triggerPriceType?: TriggerPriceType;
  // Trigger order execution type. Defaults to `Market` (StopMarket) when omitted.
  // `Limit` (StopLimit) requires either `orderPrice` or `priceMatchStrategy`.
  orderType?: TriggerOrderExecutionType;
  // Limit price applied to the resting order after the trigger fires.
  // Required when `orderType === 'Limit'` and BBO is not used.
  // Mutually exclusive with `priceMatchStrategy`.
  orderPrice?: string;
  // BBO execution: matcher stamps the limit price from the counterparty book at trigger fire time.
  // Only valid with `orderType === 'Limit'`. Mutually exclusive with `orderPrice`.
  // Value: `'PRICE_MATCH_STRATEGY_COUNTERPARTY_1'`.
  priceMatchStrategy?: string;
  orderTpTriggerPrice?: string;
  orderSlTriggerPrice?: string;
  // Order TP/SL execution type. Defaults to `Market` when omitted.
  orderTpOrderType?: TpslOrderType;
  // Required when `orderTpOrderType` is `Limit` and BBO is not used. Null otherwise.
  // Mutually exclusive with `orderTpPriceMatchStrategy`.
  orderTpLimitPrice?: string | null;
  // Only valid with `orderTpOrderType === 'Limit'`. Expresses BBO execution.
  orderTpPriceMatchStrategy?: string;
  orderSlOrderType?: TpslOrderType;
  orderSlLimitPrice?: string | null;
  orderSlPriceMatchStrategy?: string;
} & TpSlTriggerPriceTypeFields;

type TriggerTimestampOrder = {
  triggerTimestamp: Timestamp;
};

type TriggerOrder = TriggerPriceOrder | TriggerTimestampOrder;

export interface PostOrderParams extends TradeRequestBase {
  side: V2OrderSideType;
  leverage?: number;
  amount?: string;
  qty?: string;
  action: 'Open' | 'Close';
  orders: TriggerOrder[];
  slot?: number;
  marginType?: OrderMarginType;
}

export interface PostSpotMarketOrderParams extends TradeRequestBase {
  side: 'Buy' | 'Sell';
  price?: string;
  amount?: string;
  qty?: string;
  // 25q4-limit-order feature flag
  orderType?: string;
  priceMatchStrategy?: string; // newly added, defines BBO strategy, only pass it If price is null
}

export interface PostSpotMarketOrderResponse {
  asset: SpotMarketOrderAsset;
  order: {
    orderId: UUID;
    symbol: string;
    side: 'Buy' | 'Sell';
    amount?: string;
    qty?: string;
    remainingQty?: string;
  };
  timestamp: string;
}

export interface PostSpotOrderParams extends TradeRequestBase {
  side: 'Buy' | 'Sell';
  orders: {
    triggerPrice: string;
    triggerDirection: TriggerDirectionType; // 주문을 보낼때의 reference price 보다 높은지 낮은지
  }[];
  amount?: string;
  qty?: string;
}

export interface PostSpotOrderResponse {
  orders: Array<SpotOrderSuccess | ErrorResponseV2Error>;
  timestamp: TimestampString;
}

export interface PutOpenPositionParams extends TradeRequestBase {
  price: string;
  size: string;
  orderType: string;
}

export interface DeleteOpenPositionParams extends TradeRequestBase {
  side: V2OrderSideType;
  price: string;
}

export interface OrderSuccess {
  orderId: UUID;
  symbol: string;
  slot: number;
  leverage: number;
  side: 'Long' | 'Short';
  amount: string;
  size: string;
  action: 'Open' | 'Close';
  triggerPrice: string;
  triggerDirection: TriggerDirectionType;
  remainingQty?: string;
  price?: string;
  // Trigger orders only. `Market` for StopMarket, `Limit` for StopLimit. `null` for non-trigger orders.
  orderType?: TriggerOrderExecutionType | null;
  // Only populated for StopLimit (after the matcher stamps the limit price).
  orderPrice?: string | null;
  // Only populated when the order was submitted with `priceMatchStrategy` (BBO).
  priceMatchStrategy?: string | null;
  triggerPriceType?: TriggerPriceType;
  orderTpTriggerPriceType?: TriggerPriceType;
  orderSlTriggerPriceType?: TriggerPriceType;
}

export interface SpotOrderSuccess {
  orderId: UUID;
  symbol: string;
  side: 'Buy' | 'Sell';
  amount: string;
  qty: string;
  triggerPrice: string;
  triggerDirection: TriggerDirectionType;
}

export interface PostOrderResponse {
  orders: Array<OrderSuccess | ErrorResponseV2Error>;
  timestamp: TimestampString;
}

interface ClosedPosition {
  positionId: string;
  symbol: string;
  slot: number;
  entryPrice: string;
  closePrice: string;
  size: string;
  pnl: string;
  remainingQty?: string;
  remainingPositionSize?: string;
}

export interface PutOpenPositionResponse {
  position?: ClosedPosition;
  order: OrderSuccess;
  timestamp: string;
}

export interface DeleteOpenPositionResponse {
  position: ClosedPosition;
  timestamp: string;
}

export type DeletePendingPositionParams = TradeRequestBase;

export interface DeletePendingPositionResponse {
  timestamp: string;
}

export interface PutInstantFlipParams extends TradeRequestBase {
  side: V2OrderSideType;
  price: string;
}

/**
 * Execution-type fields on the amend `trigger` body.
 *
 * Retail-api amend: omitting any of these serialises as the server-side default
 * (Market / null); the caller is responsible for re-sending the existing values
 * to avoid silently downgrading a Limit TP/SL to Market. `limitPrice` and
 * `priceMatchStrategy` are mutually exclusive per side.
 */
interface TpslTriggerOrderTypeFields extends TpSlTriggerPriceTypeFields {
  orderTpOrderType?: TpslOrderType;
  orderTpLimitPrice?: string | null;
  orderTpPriceMatchStrategy?: string | null;
  orderSlOrderType?: TpslOrderType;
  orderSlLimitPrice?: string | null;
  orderSlPriceMatchStrategy?: string | null;
}

export interface PutOrderTpSlParams {
  requestId: string; // uuid
  timestamp: string;
  type: 'OrderTp' | 'OrderSl';
  trigger?:
    | ({
        triggerPrice: string;
        triggerDirection: TriggerDirectionType;
        triggerPriceType?: TriggerPriceType;
      } & TpslTriggerOrderTypeFields)
    | ({
        triggerPriceRatio: string;
        triggerDirection: TriggerDirectionType;
        triggerPriceType?: TriggerPriceType;
      } & TpslTriggerOrderTypeFields);
  attribution?: string;
  /** Sub-account ID for copy trading. If omitted, treated as the main account's request. */
  accountId?: number;
}

export interface DeleteMultiOpenPositionParams extends TradeRequestBase {
  positions: {
    symbol: string;
    slot: number;
    side: V2OrderSideType;
    price: string;
  }[];
}

type DeleteMultiOpenPositionStatus =
  | 'Success'
  | 'InvalidPrice'
  | 'InvalidRequest'
  | 'InvalidOrderStatus'
  | 'NotEnoughBalance'
  | 'RiskOfLiquidation'
  | 'UnknownPosition'
  | 'LockedPosition'
  | 'RejectedAroundFundingTime'
  | 'PartiallyFilled'
  | 'TradingSessionSuspended';

export interface DeleteMultiOpenPositionResponse {
  positions: {
    symbol: string;
    slot: number;
    entryPrice: string;
    closePrice: string;
    size: string;
    pnl: string;
    status: DeleteMultiOpenPositionStatus;
  }[];
  timestamp: string;
}

export interface PutAdjustMarginParams extends TradeRequestBase {
  amount: string;
}

export interface PutAdjustLeverageParams extends TradeRequestBase {
  leverage: number;
}

export type TradeMode = 'MultiplePositions' | 'OneWay';

export interface PutTradeModeParams {
  mode: TradeMode;
}

export interface PostOneWayOrderParams extends OneWayOrderCreationRequestBase, TpSlTriggerPriceTypeFields {
  leverage?: number;
  side: V2OrderSideType;
  price?: string;
  amount?: string;
  qty?: string;
  orderType?: string;
  priceMatchStrategy?: string; // newly added, defines BBO strategy, only pass it If price is null

  orderTpTriggerPrice?: string;
  orderSlTriggerPrice?: string;
  // Order TP/SL execution type. Defaults to `Market` when omitted.
  orderTpOrderType?: TpslOrderType;
  // Required when `orderTpOrderType` is `Limit` and BBO is not used. Null otherwise.
  // Mutually exclusive with `orderTpPriceMatchStrategy`.
  orderTpLimitPrice?: string | null;
  // Only valid with `orderTpOrderType === 'Limit'`. Expresses BBO execution.
  orderTpPriceMatchStrategy?: string;
  orderSlOrderType?: TpslOrderType;
  orderSlLimitPrice?: string | null;
  orderSlPriceMatchStrategy?: string;
  autoSlTriggerPriceRatio?: string; // Range: 0 < ratio ≤ 0.9
  // ── Max slippage (ERD-233) · market orders only · send at most ONE; omit both => unchanged behavior ──
  // By percent: decimal RATIO (0.011 = 1.1%), server validates to [0.0001, 0.05].
  maxSlippageTolerance?: string;
  // By price: absolute worst execution price (quote ccy), client-clamped to mid ±5%.
  marketOrderLimitPrice?: string;
}

export interface PostOneWayOrderResponse {
  order: OrderSuccess;
  timestamp: string;
}

export interface PostOneWayCloseOrderSymbolParams extends TradeRequestBase {
  side: V2OrderSideType;
  price: string;
}

export interface PostOneWayCloseOrderParams extends TradeRequestBase {
  positions: {
    symbol: string;
    side: V2OrderSideType;
    price: string;
  }[];
  /**
   * It provides only for mobile airdrop positions.
   * But API needs to provide airdropPositions.
   */
  airdropPositions: {
    symbol: string;
    slot: number;
    side: V2OrderSideType;
    price: string;
  }[];
}

export interface OneWayClosePosition {
  positionId: string;
  symbol: string;
  slot: number;
  entryPrice: string;
  closePrice: string;
  remainingQty?: string;
  remainingPositionSize?: string;
  size: string;
  pnl: string;
  status: DeleteMultiOpenPositionStatus;
}

export interface PostOneWayCloseOrderSymbolResponse {
  position: OneWayClosePosition;
  timestamp: Timestamp;
}

export interface PostOneWayCloseOrderResponse {
  positions: OneWayClosePosition[];
  timestamp: Timestamp;
}

type OneWayConditionalTriggerPriceOrder = {
  triggerPrice: string;
  triggerDirection: TriggerDirectionType;
  triggerPriceType?: TriggerPriceType;
};

type OneWayConditionalTriggerTimestampOrder = {
  triggerTimestamp: Timestamp;
};

type OneWayConditionalTriggerOrder = OneWayConditionalTriggerPriceOrder | OneWayConditionalTriggerTimestampOrder;

export type PostOneWayConditionalOrderParams = OneWayOrderCreationRequestBase &
  OneWayConditionalTriggerOrder & {
    leverage?: number;
    side: V2OrderSideType;
    amount?: string;
    qty?: string;
    // Trigger order execution type. Defaults to `Market` (StopMarket) when omitted.
    // `Limit` (StopLimit) requires either `orderPrice` or `priceMatchStrategy`.
    // Rejected when set together with `triggerTimestamp` (time-trigger orders are Market-only).
    orderType?: TriggerOrderExecutionType;
    // Limit price applied to the resting order after the trigger fires.
    // Required when `orderType === 'Limit'` and BBO is not used.
    // Mutually exclusive with `priceMatchStrategy`.
    orderPrice?: string;
    // BBO execution: matcher stamps the limit price from the counterparty book at trigger fire time.
    // Only valid with `orderType === 'Limit'`. Mutually exclusive with `orderPrice`.
    // Value: `'PRICE_MATCH_STRATEGY_COUNTERPARTY_1'`.
    priceMatchStrategy?: string;
    // Bracket TP/SL trigger prices. Rejected when only `triggerTimestamp` is set.
    orderTpTriggerPrice?: string | null;
    orderSlTriggerPrice?: string | null;
    // Order TP/SL execution type. Defaults to `Market` when omitted.
    orderTpOrderType?: TpslOrderType;
    // Required when `orderTpOrderType` is `Limit` and BBO is not used. Null otherwise.
    // Mutually exclusive with `orderTpPriceMatchStrategy`.
    orderTpLimitPrice?: string | null;
    // Only valid with `orderTpOrderType === 'Limit'`. Expresses BBO execution.
    orderTpPriceMatchStrategy?: string | null;
    orderSlOrderType?: TpslOrderType;
    orderSlLimitPrice?: string | null;
    orderSlPriceMatchStrategy?: string | null;
  } & TpSlTriggerPriceTypeFields;

export interface PostOneWayConditionalOrderResponse {
  order: OrderSuccess | ErrorResponseV2Error;
  timestamp: TimestampString;
}

export interface PostOneWayFlipParams extends TradeRequestBase {
  side: V2OrderSideType;
  price: string;
}

export interface PostOneWayTpslParams extends TradeRequestBase {
  side: V2OrderSideType;
  triggerPrice: string;
  triggerDirection: TriggerDirectionType;
  triggerPriceType?: TriggerPriceType;
}

export interface PostProfitBoostParams {
  requestId: string;
  cost: string;
  durationInMin: number;
  attribution?: string;
}

export interface PostOrderPartialTpslParams {
  requestId: string;
  timestamp: Timestamp;
  attribution?: string;
  slot?: number;
  side: V2OrderSideType;
  orders: {
    triggerPrice?: string;
    triggerDirection?: TriggerDirectionType;
    triggerPriceType?: TriggerPriceType;
    triggerTimestamp?: Timestamp;
    orderTpTriggerPrice?: string;
    orderSlTriggerPrice?: string;
    orderTpTriggerPriceType?: TriggerPriceType;
    orderSlTriggerPriceType?: TriggerPriceType;
    orderQty?: string;
    // Limit TP execution. Defaults to Market when omitted.
    orderTpOrderType?: TpslOrderType;
    // User-set limit price. Mutually exclusive with `orderTpPriceMatchStrategy`.
    // Null for Market, or when expressing BBO via `orderTpPriceMatchStrategy`.
    orderTpLimitPrice?: string | null;
    // Only valid with `orderTpOrderType === 'Limit'`. Expresses BBO execution.
    // Mutually exclusive with `orderTpLimitPrice`.
    orderTpPriceMatchStrategy?: string | null;
    orderSlOrderType?: TpslOrderType;
    orderSlLimitPrice?: string | null;
    orderSlPriceMatchStrategy?: string | null;
  }[];
  refServerTimestamp?: Timestamp;
  refClientTimestamp?: Timestamp;
  /** Sub-account ID for copy trading. If omitted, treated as the main account's request. */
  accountId?: number;
}

export interface PostOrderPartialTpSlResponse {
  orders: OrderSuccess;
  timestamp: TimestampString;
}

export interface PutOrderPartialTpslParams {
  requestId: string;
  timestamp: Timestamp;
  attribution?: string;
  /** Sub-account ID for copy trading. If omitted, treated as the main account's request. */
  accountId?: number;
  type: 'OrderTp' | 'OrderSl';
  trigger?: {
    triggerPrice?: string;
    triggerDirection?: TriggerDirectionType;
    triggerPriceType?: TriggerPriceType;
    triggerPriceRatio?: string;
    orderQty?: string;
    // Retail-api amend: omitting this field (or the matching limitPrice/priceMatchStrategy)
    // is serialised as `Market` / `null`. Caller must re-send the existing execution type
    // and the existing limitPrice OR priceMatchStrategy to avoid silently downgrading a
    // Limit TP/SL to Market.
    orderTpOrderType?: TpslOrderType;
    // Mutually exclusive with `orderTpPriceMatchStrategy`.
    orderTpLimitPrice?: string | null;
    // Only valid with `orderTpOrderType === 'Limit'`. Expresses BBO execution.
    orderTpPriceMatchStrategy?: string | null;
    orderSlOrderType?: TpslOrderType;
    orderSlLimitPrice?: string | null;
    orderSlPriceMatchStrategy?: string | null;
  };
}

export interface PutOrderPartialTpslResponse {
  order: {
    orderId: string;
    orderTpTriggerPrice?: string;
    orderSlTriggerPrice?: string;
    orderTpTriggerPriceRatio?: string;
    orderSlTriggerPriceRatio?: string;
    triggerPriceType?: TriggerPriceType;
    orderTpTriggerPriceType?: TriggerPriceType;
    orderSlTriggerPriceType?: TriggerPriceType;
  };
  timestamp: Timestamp;
}

export interface PostOneWayPartialTpslParams {
  requestId: string;
  timestamp: Timestamp;
  attribution?: string;
  side: V2OrderSideType;
  orderQty?: string;
  triggerPrice: string;
  triggerDirection: TriggerDirectionType;
  triggerPriceType?: TriggerPriceType;
  /**
   * One-way partial-tpsl places a single standalone TP/SL per call, so fields are
   * unprefixed (no orderTp* / orderSl* split). Defaults to `Market` when omitted.
   */
  orderType?: TpslOrderType;
  /** Required when `orderType === 'Limit'` and `priceMatchStrategy` is not set. */
  limitPrice?: string | null;
  /** Mutually exclusive with `limitPrice`. `PRICE_MATCH_STRATEGY_COUNTERPARTY_1` for BBO. */
  priceMatchStrategy?: string | null;
  refServerTimestamp?: Timestamp;
  refClientTimestamp?: Timestamp;
  /** Sub-account ID for copy trading. If omitted, treated as the main account's request. */
  accountId?: number;
}

export interface PostOneWayPartialTpSlResponse {
  order: OrderSuccess;
  timestamp: Timestamp;
}

export interface DeleteAllTpslOrdersApiParams {
  requestId: string;
  timestamp: Timestamp;
  conditionalOrderType: typeof ConditionalOrderTypeEnum.PositionTP | typeof ConditionalOrderTypeEnum.PositionSL;
  symbol: string;
  slot?: number;
}

export interface DeleteAllTpslOrdersApiResponse {
  orders: { orderId: string; success: boolean }[];
  timestamp: Timestamp;
}

export interface PutAmendOrderPriceParams extends TradeRequestBase {
  price?: string;
  triggerPrice?: string;
  side?: V2OrderSideType;
  qty?: string;
  amount?: string;
}

export interface PutAmendOrderPriceResponse {
  order: {
    orderId: UUID;
    price: string | null;
    triggerPrice: string | null;
  };
  timestamp: string;
}
